(() => {
  if (window.__bfCatalogNavigationInitialized) return;
  window.__bfCatalogNavigationInitialized = true;
  if (window.Shopify?.designMode) return;

  const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
  const loadedExternalScripts = new Set(
    Array.from(document.scripts)
      .filter((script) => script.src)
      .map((script) => new URL(script.src, window.location.href).href)
  );
  const executedInlineScripts = new Set(
    Array.from(document.scripts)
      .filter((script) => !script.src && isExecutableScript(script))
      .map(inlineScriptKey)
  );

  let activeController = null;

  function isExecutableScript(script) {
    const type = (script.getAttribute('type') || '').trim().toLowerCase();
    return !type || type === 'text/javascript' || type === 'application/javascript' || type === 'module';
  }

  function inlineScriptKey(script) {
    return `${(script.getAttribute('type') || '').trim().toLowerCase()}::${script.textContent || ''}`;
  }

  function audioIsPlaying() {
    const audio = window.__bfCurrentAudio;
    return Boolean(audio && !audio.paused && !audio.ended);
  }

  function withPreviewTheme(input) {
    const url = new URL(input, window.location.href);
    if (previewThemeId && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewThemeId);
    }
    return url;
  }

  function normalizedStorePath(pathname) {
    const normalized = pathname.replace(/\/{2,}/g, '/');
    const withoutLocale = normalized.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '');
    return withoutLocale || '/';
  }

  function isSeamlessStoreUrl(url) {
    if (url.origin !== window.location.origin) return false;

    const path = normalizedStorePath(url.pathname);
    return (
      path === '/' ||
      path === '/products' ||
      path.startsWith('/products/') ||
      path === '/collections' ||
      path.startsWith('/collections/') ||
      path === '/pages' ||
      path.startsWith('/pages/') ||
      path === '/policies' ||
      path.startsWith('/policies/')
    );
  }

  function hasCollectionUiParams(url) {
    const path = normalizedStorePath(url.pathname);
    if (!path.startsWith('/collections')) return false;

    for (const key of url.searchParams.keys()) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') return true;
    }
    return false;
  }

  function shouldHandleAnchor(anchor, event) {
    if (!anchor || event.defaultPrevented) return false;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (anchor.target && anchor.target.toLowerCase() !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;
    if (
      anchor.closest(
        'facet-filters-form, .facets-container, .facets__form, .pagination-wrapper, cart-drawer, #CartDrawer, cart-notification, localization-form, predictive-search, search-modal'
      )
    ) {
      return false;
    }

    const rawHref = anchor.getAttribute('href');
    if (
      !rawHref ||
      rawHref.startsWith('#') ||
      rawHref.startsWith('mailto:') ||
      rawHref.startsWith('tel:') ||
      rawHref.startsWith('javascript:')
    ) {
      return false;
    }

    const url = withPreviewTheme(anchor.href);
    if (!isSeamlessStoreUrl(url) || hasCollectionUiParams(url)) return false;

    const current = withPreviewTheme(window.location.href);
    if (url.pathname === current.pathname && url.search === current.search && url.hash === current.hash) return false;
    return true;
  }

  function markCurrentHistoryEntry() {
    const previous = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState(
      {
        ...previous,
        __bfCatalogNav: true,
        __bfCatalogUrl: window.location.href,
        __bfScrollY: window.scrollY,
      },
      '',
      window.location.href
    );
  }

  function closePersistentShellUi() {
    document.querySelectorAll('header details[open], menu-drawer details[open]').forEach((details) => {
      details.removeAttribute('open');
    });
    document.querySelectorAll('.menu-opening').forEach((node) => node.classList.remove('menu-opening'));
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet');
    document.documentElement.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet');
  }

  function cleanupMovedPageModals(oldMain) {
    const sectionTokens = Array.from(oldMain.querySelectorAll('[id^="shopify-section-"]'))
      .map((node) => node.id.replace(/^shopify-section-/, ''))
      .filter(Boolean);

    if (!sectionTokens.length) return;

    document.querySelectorAll('body > product-modal, body > quick-add-modal, body > modal-dialog').forEach((modal) => {
      const marker = `${modal.id || ''} ${modal.getAttribute('data-section') || ''}`;
      if (sectionTokens.some((token) => marker.includes(token))) modal.remove();
    });
  }

  function copyScriptAttributes(from, to) {
    Array.from(from.attributes).forEach((attribute) => {
      to.setAttribute(attribute.name, attribute.value);
    });
  }

  async function activateScripts(root) {
    const scripts = Array.from(root.querySelectorAll('script'));

    for (const inertScript of scripts) {
      if (!isExecutableScript(inertScript)) continue;

      if (inertScript.src) {
        const src = new URL(inertScript.src, window.location.href).href;
        if (loadedExternalScripts.has(src)) {
          inertScript.remove();
          continue;
        }

        await new Promise((resolve, reject) => {
          const liveScript = document.createElement('script');
          copyScriptAttributes(inertScript, liveScript);
          liveScript.addEventListener(
            'load',
            () => {
              loadedExternalScripts.add(src);
              resolve();
            },
            { once: true }
          );
          liveScript.addEventListener('error', () => reject(new Error(`Script failed: ${src}`)), { once: true });
          inertScript.replaceWith(liveScript);
        });
        continue;
      }

      const key = inlineScriptKey(inertScript);
      if (executedInlineScripts.has(key)) {
        inertScript.remove();
        continue;
      }

      const liveScript = document.createElement('script');
      copyScriptAttributes(inertScript, liveScript);
      liveScript.textContent = inertScript.textContent;
      inertScript.replaceWith(liveScript);
      executedInlineScripts.add(key);
    }
  }

  function syncDocumentMetadata(nextDocument) {
    if (nextDocument.title) document.title = nextDocument.title;

    const nextDescription = nextDocument.querySelector('meta[name="description"]');
    const currentDescription = document.querySelector('meta[name="description"]');
    if (nextDescription && currentDescription) {
      currentDescription.setAttribute('content', nextDescription.getAttribute('content') || '');
    } else if (nextDescription && !currentDescription) {
      document.head.appendChild(nextDescription.cloneNode(true));
    } else if (!nextDescription && currentDescription) {
      currentDescription.remove();
    }

    const nextCanonical = nextDocument.querySelector('link[rel="canonical"]');
    const currentCanonical = document.querySelector('link[rel="canonical"]');
    if (nextCanonical && currentCanonical) {
      currentCanonical.href = nextCanonical.href;
    }

    if (nextDocument.documentElement.lang) {
      document.documentElement.lang = nextDocument.documentElement.lang;
    }
  }

  async function navigate(input, options = {}) {
    const targetUrl = withPreviewTheme(input);
    const fromPopState = Boolean(options.fromPopState);
    const pushState = options.pushState !== false;
    const scrollY = Number.isFinite(options.scrollY) ? options.scrollY : 0;

    if (!isSeamlessStoreUrl(targetUrl)) {
      window.location.href = targetUrl.href;
      return;
    }

    if (!fromPopState && !audioIsPlaying()) {
      window.location.href = targetUrl.href;
      return;
    }

    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;

    const oldMain = document.querySelector('#MainContent');
    if (!oldMain) {
      window.location.href = targetUrl.href;
      return;
    }

    if (pushState) markCurrentHistoryEntry();
    oldMain.setAttribute('aria-busy', 'true');
    document.documentElement.classList.add('bf-catalog-is-navigating');

    try {
      const response = await fetch(targetUrl.href, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Page request failed: ${response.status}`);

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextMainSource = nextDocument.querySelector('#MainContent');
      if (!nextMainSource) throw new Error('Destination MainContent was not found');

      window.__bfKeepCurrentAudioAlive?.(oldMain);
      cleanupMovedPageModals(oldMain);
      const nextMain = document.importNode(nextMainSource, true);
      oldMain.replaceWith(nextMain);

      await activateScripts(nextMain);
      syncDocumentMetadata(nextDocument);
      closePersistentShellUi();
      window.Shopify?.PaymentButton?.init?.();

      if (pushState) {
        history.pushState(
          {
            __bfCatalogNav: true,
            __bfCatalogUrl: targetUrl.href,
            __bfScrollY: 0,
          },
          '',
          targetUrl.href
        );
      }

      window.scrollTo({ top: Math.max(0, scrollY), left: 0, behavior: 'auto' });
      window.dispatchEvent(
        new CustomEvent('bf:catalog:navigated', {
          detail: { url: targetUrl.href, main: nextMain },
        })
      );
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (fromPopState) {
        window.location.reload();
      } else {
        window.location.href = targetUrl.href;
      }
    } finally {
      if (activeController === controller) activeController = null;
      document.documentElement.classList.remove('bf-catalog-is-navigating');
      document.querySelector('#MainContent')?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!shouldHandleAnchor(anchor, event)) return;
      if (!audioIsPlaying()) return;

      event.preventDefault();
      navigate(anchor.href, { pushState: true, fromPopState: false, scrollY: 0 });
    },
    true
  );

  window.addEventListener(
    'popstate',
    (event) => {
      if (!event.state?.__bfCatalogNav) return;
      if (!isSeamlessStoreUrl(withPreviewTheme(window.location.href))) return;

      event.stopImmediatePropagation();
      navigate(window.location.href, {
        pushState: false,
        fromPopState: true,
        scrollY: Number(event.state.__bfScrollY || 0),
      });
    },
    true
  );
})();
